import React from 'react';

interface Props {
  backtestPeriod: '15m' | '30m' | '1h' | '4h';
  setBacktestPeriod: (period: '15m' | '30m' | '1h' | '4h') => void;
}

export const SignalSidePanelBacktest: React.FC<Props> = ({ backtestPeriod, setBacktestPeriod }) => {
  return (
    <div className="space-y-5">
      <div className="bg-muted/50 p-3 rounded-xl border border-border/50 text-[11px] space-y-1">
        <p className="font-bold text-muted-foreground flex justify-between">
          <span>종목: <strong className="text-foreground">ETHUSDT</strong></span>
          <span>비중: <strong className="text-foreground">10%</strong></span>
          <span>레버리지: <strong className="text-foreground">20x</strong></span>
          <span>수수료: <strong className="text-foreground">0.05%</strong></span>
        </p>
        <p className="text-[10px] text-muted-foreground/80 italic mt-0.5">※ 2026-06-01 최종 시뮬레이션 결과 기준 (30일, auto_signal/ict/hybrid_pullback 신규 추가)</p>
      </div>

      <div className="flex gap-2">
        {(['15m', '30m', '1h', '4h'] as const).map((period) => (
          <button
            key={period}
            type="button"
            onClick={() => setBacktestPeriod(period)}
            className={`flex-1 py-1 text-[10px] font-bold rounded-md border transition-all ${backtestPeriod === period
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background hover:bg-muted text-muted-foreground border-border'
              }`}
          >
            {period === '15m' ? '15분봉' : period === '30m' ? '30분봉' : period === '1h' ? '1시간봉' : '4시간봉'}
          </button>
        ))}
      </div>

      <div className="border border-border/60 rounded-xl overflow-hidden">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-muted/40 border-b border-border font-bold text-muted-foreground text-[10px]">
              <th className="p-2">순위 / 전략</th>
              <th className="p-2 text-right">수익률</th>
              <th className="p-2 text-right">승률</th>
              <th className="p-2 text-right">거래수</th>
              <th className="p-2 text-right">MDD</th>
              <th className="p-2 text-right">설정</th>
            </tr>
          </thead>
          <tbody>
            {backtestPeriod === '15m' && (
              <>
                <tr className="border-b border-border/40 font-semibold">
                  <td className="p-2">1. super_hybrid</td>
                  <td className="p-2 text-right text-success">+23.51%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">11.1%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">SL/TP기본</td>
                </tr>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">2. auto_signal</td>
                  <td className="p-2 text-right text-success">+13.68%</td>
                  <td className="p-2 text-right">62.5%</td>
                  <td className="p-2 text-right">12</td>
                  <td className="p-2 text-right text-danger">8.08%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">EMA+Ichi+RSI</td>
                </tr>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">3. macd_ema</td>
                  <td className="p-2 text-right text-success">+10.42%</td>
                  <td className="p-2 text-right">38.9%</td>
                  <td className="p-2 text-right">108</td>
                  <td className="p-2 text-right text-danger">9.75%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">EMA20</td>
                </tr>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">4. zerolag_macd</td>
                  <td className="p-2 text-right text-success">+8.33%</td>
                  <td className="p-2 text-right">46.4%</td>
                  <td className="p-2 text-right">112</td>
                  <td className="p-2 text-right text-danger">6.32%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">MA200</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">5. channel_trend2</td>
                  <td className="p-2 text-right text-success">+3.09%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">1.77%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">LR100</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">6. channel_trend</td>
                  <td className="p-2 text-right text-success">+0.86%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">0.81%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">LR100</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">7. kewma</td>
                  <td className="p-2 text-right text-danger">-6.70%</td>
                  <td className="p-2 text-right">47.8%</td>
                  <td className="p-2 text-right">46</td>
                  <td className="p-2 text-right text-danger">18.6%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">KEWMA45</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">8. ict</td>
                  <td className="p-2 text-right text-danger">-6.86%</td>
                  <td className="p-2 text-right">82.8%</td>
                  <td className="p-2 text-right">130</td>
                  <td className="p-2 text-right text-danger">15.3%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">FVG+OB+MSS</td>
                </tr>
                <tr>
                  <td className="p-2 text-muted-foreground">9. hybrid_pullback</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">ADX≥20</td>
                </tr>
              </>
            )}
            {backtestPeriod === '30m' && (
              <>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">1. channel_trend2</td>
                  <td className="p-2 text-right text-success">+25.54%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">7.48%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">LR100</td>
                </tr>
                <tr className="border-b border-border/40 font-semibold">
                  <td className="p-2">2. channel_trend</td>
                  <td className="p-2 text-right text-success">+23.51%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">7.69%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">LR100</td>
                </tr>
                <tr className="border-b border-border/40 font-semibold">
                  <td className="p-2">3. super_hybrid</td>
                  <td className="p-2 text-right text-success">+21.53%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">13.7%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">SL/TP기본</td>
                </tr>
                <tr className="border-b border-border/40 bg-success/5">
                  <td className="p-2">4. auto_signal</td>
                  <td className="p-2 text-right text-success">+11.04%</td>
                  <td className="p-2 text-right">66.7%</td>
                  <td className="p-2 text-right">9</td>
                  <td className="p-2 text-right text-danger">9.01%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">EMA+Ichi+RSI</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">5. kewma</td>
                  <td className="p-2 text-right text-success">+2.07%</td>
                  <td className="p-2 text-right">53.3%</td>
                  <td className="p-2 text-right">30</td>
                  <td className="p-2 text-right text-danger">11.1%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">KEWMA45</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">6. zerolag_macd</td>
                  <td className="p-2 text-right text-success">+1.07%</td>
                  <td className="p-2 text-right">48.2%</td>
                  <td className="p-2 text-right">58</td>
                  <td className="p-2 text-right text-danger">9.29%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">MA200</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">7. ict</td>
                  <td className="p-2 text-right text-danger">-0.42%</td>
                  <td className="p-2 text-right">91.4%</td>
                  <td className="p-2 text-right">45</td>
                  <td className="p-2 text-right text-danger">6.92%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">FVG+OB+MSS</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">8. macd_ema</td>
                  <td className="p-2 text-right text-danger">-13.30%</td>
                  <td className="p-2 text-right">27.3%</td>
                  <td className="p-2 text-right">66</td>
                  <td className="p-2 text-right text-danger">13.3%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">EMA20</td>
                </tr>
                <tr>
                  <td className="p-2 text-muted-foreground">9. hybrid_pullback</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">ADX≥20</td>
                </tr>
              </>
            )}
            {backtestPeriod === '1h' && (
              <>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">1. auto_signal</td>
                  <td className="p-2 text-right text-success">+18.43%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">3</td>
                  <td className="p-2 text-right text-danger">5.53%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">EMA+Ichi+RSI</td>
                </tr>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">2. macd_ema</td>
                  <td className="p-2 text-right text-success">+16.76%</td>
                  <td className="p-2 text-right">66.6%</td>
                  <td className="p-2 text-right">12</td>
                  <td className="p-2 text-right text-danger">3.13%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">EMA20</td>
                </tr>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">3. zerolag_macd</td>
                  <td className="p-2 text-right text-success">+9.43%</td>
                  <td className="p-2 text-right">75.0%</td>
                  <td className="p-2 text-right">8</td>
                  <td className="p-2 text-right text-danger">1.90%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">MA200</td>
                </tr>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">4. stochastic_ema</td>
                  <td className="p-2 text-right text-success">+1.44%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">4</td>
                  <td className="p-2 text-right text-danger">0.42%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">Stoch+EMA+ADX</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">5. kewma</td>
                  <td className="p-2 text-right text-danger">-8.19%</td>
                  <td className="p-2 text-right">0.0%</td>
                  <td className="p-2 text-right">6</td>
                  <td className="p-2 text-right text-danger">8.19%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">KEWMA45</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">6. super_hybrid</td>
                  <td className="p-2 text-right text-danger">-28.26%</td>
                  <td className="p-2 text-right">0.0%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">33.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">SL/TP기본</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2 text-muted-foreground">7. channel_trend</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">LR100</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2 text-muted-foreground">8. channel_trend2</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">LR100</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2 text-muted-foreground">9. ict</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">FVG+OB+MSS</td>
                </tr>
                <tr>
                  <td className="p-2 text-muted-foreground">10. hybrid_pullback</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">ADX≥20</td>
                </tr>
              </>
            )}
            {backtestPeriod === '4h' && (
              <>
                <tr className="border-b border-border/40 bg-success/5 font-bold">
                  <td className="p-2">1. super_hybrid</td>
                  <td className="p-2 text-right text-success">+23.89%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">10.2%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">SL/TP기본</td>
                </tr>
                <tr className="border-b border-border/40 font-semibold">
                  <td className="p-2">2. channel_trend</td>
                  <td className="p-2 text-right text-success">+9.68%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">1.98%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">LR100</td>
                </tr>
                <tr className="border-b border-border/40 font-semibold">
                  <td className="p-2">3. kewma</td>
                  <td className="p-2 text-right text-success">+6.16%</td>
                  <td className="p-2 text-right">100%</td>
                  <td className="p-2 text-right">2</td>
                  <td className="p-2 text-right text-danger">0.12%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">KEWMA45</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">4. zerolag_macd</td>
                  <td className="p-2 text-right text-success">+4.94%</td>
                  <td className="p-2 text-right">75.0%</td>
                  <td className="p-2 text-right">8</td>
                  <td className="p-2 text-right text-danger">5.48%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">MA200</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2">5. macd_ema</td>
                  <td className="p-2 text-right text-danger">-5.70%</td>
                  <td className="p-2 text-right">50.0%</td>
                  <td className="p-2 text-right">12</td>
                  <td className="p-2 text-right text-danger">15.9%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">EMA20</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2 text-muted-foreground">6. auto_signal</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">EMA+Ichi+RSI</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2 text-muted-foreground">7. channel_trend2</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">LR100</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="p-2 text-muted-foreground">8. ict</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">FVG+OB+MSS</td>
                </tr>
                <tr>
                  <td className="p-2 text-muted-foreground">9. hybrid_pullback</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right">-</td>
                  <td className="p-2 text-right">0</td>
                  <td className="p-2 text-right text-foreground">0.0%</td>
                  <td className="p-2 text-right text-muted-foreground text-[9px]">ADX≥20</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <details className="group text-xs">
        <summary className="cursor-pointer text-[10px] font-bold text-muted-foreground hover:text-foreground transition-colors px-1 py-1 select-none">
          ▶ 백테스트 실행 명령어
        </summary>
        <div className="mt-2 border border-border/60 rounded-xl overflow-hidden">
          <table className="w-full text-left border-collapse text-[10px]">
            <thead>
              <tr className="bg-muted/40 border-b border-border font-bold text-muted-foreground">
                <th className="p-1.5">전략</th>
                <th className="p-1.5">명령어</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">auto_signal</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy auto_signal --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">super_hybrid</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy super_hybrid --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">macd_ema</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy macd_ema --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">zerolag_macd</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy zerolag_macd --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">kewma</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy kewma --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">channel_trend</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy channel_trend --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">channel_trend2</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy channel_trend2_strategy --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>

              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">ict</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy ict --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">stochastic_ema</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy stochastic_ema --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
              <tr className="border-b border-border/40"><td className="p-1.5 font-medium">perfect_alignment_pullback</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy perfect_alignment_pullback --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
                            <tr><td className="p-1.5 font-medium">hybrid_pullback</td><td className="p-1.5 font-mono text-[9px] break-all">{"ruby bin/backtest.rb --strategy hybrid_pullback --symbol ETHUSDT --interval {TF} --days 30 --leverage 20"}</td></tr>
            </tbody>
          </table>
          <div className="p-2 bg-muted/30 text-[9px] text-muted-foreground leading-relaxed">
            <p><b>공통 옵션:</b> {"{TF}"}=15m/30m/1h, {"{d}"}=30 / {"{TF}"}=4h, {"{d}"}=40</p>
            <p className="mt-0.5"><b>비고:</b> hybid_pullback은 ETH 단일종목 시그널 0건. 포트폴리오 스크립트: <span className="font-mono">ruby bin/backtest_hybrid_pullback_portfolio.rb</span></p>
          </div>
        </div>
      </details>

      <div className="space-y-2">
        <h4 className="text-[11px] font-black text-muted-foreground uppercase px-1">최종 권장 등급</h4>
        <p className="text-[9px] text-muted-foreground">※ TP/SL을 ATR 기반으로 계산하는 전략: auto_signal, auto2_signal, auto3_signal, auto4_signal, auto5_signal, auto6_signal, hybrid_pullback</p>
        <div className="space-y-2.5">
          <div className="p-3 bg-primary/10 border border-primary/20 rounded-xl flex gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">1</div>
            <div className="space-y-0.5">
              <p className="text-xs font-black">CenIchi 4Ema Pullback (auto_signal) (15분봉 / 1시간봉)</p>
              <p className="text-[10px] text-muted-foreground">15m: +13.68% (12회, Sharpe 0.78), 1h: +18.43% (3회, Sharpe 2.38)로 전 TF 흑자 신규 강력 추천</p>
              <p className="text-[9px] text-muted-foreground">※ 이 전략은 TP 목표가를 ATR 기반으로 계산합니다. TP와 SL은 ATR × multiplier(0.5, 1.5, 2.5, 4.0)로 설정됩니다.</p>
            </div>
          </div>
          <div className="p-3 bg-muted/70 border border-border/60 rounded-xl flex gap-3">
            <div className="w-6 h-6 rounded-full bg-muted/80 text-foreground flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">2</div>
            <div className="space-y-0.5">
              <p className="text-xs font-black">ZeroLag MACD (zerolag_macd) (15분봉 / 1시간봉)</p>
              <p className="text-[10px] text-muted-foreground">15m: +8.33% (112회), 1h: +9.43% (8회, Sharpe 2.97)로 전 TF 흑자 달성한 가장 안정적인 전략</p>
            </div>
          </div>
          <div className="p-3 bg-muted/70 border border-border/60 rounded-xl flex gap-3">
            <div className="w-6 h-6 rounded-full bg-muted/80 text-foreground flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">3</div>
            <div className="space-y-0.5">
              <p className="text-xs font-black">MACD + EMA 20 (macd_ema) (15분봉 / 1시간봉)</p>
              <p className="text-[10px] text-muted-foreground">15m: +10.42% (108회), 1h: +16.76% (12회)로 30m봉을 제외하고 풍부한 거래량과 고수익 확보</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
